/* ── Admin Minio Orchestrator & Renderer (ES Module) ── */
import { catalog, selectedChannel, _minioPrefix, _minioAllTags, _minioSearchTimer, updateState } from './admin-globals.js';
import { escHtml, showToast } from './admin-ui-base.js';
import * as API from './admin-minio-api.js';

/**
 * Abre el explorador de Minio
 */
export async function openMinioModal() {
  document.getElementById('minio-modal').classList.remove('hidden');
  document.getElementById('minio-search-input').value = '';
  const allTags = await API.fetchMinioAllTags();
  updateState('_minioAllTags', allTags);
  browseMinio('');
}

export function closeMinioModal() {
  document.getElementById('minio-modal').classList.add('hidden');
}

export function onMinioSearchInput(q) {
  clearTimeout(_minioSearchTimer);
  if (!q.trim()) { browseMinio(_minioPrefix); return; }
  const timer = setTimeout(() => searchMinio(q.trim()), 400);
  updateState('_minioSearchTimer', timer);
}

export async function searchMinio(q) {
  const resultsEl = document.getElementById('minio-results');
  document.getElementById('minio-breadcrumb').innerHTML = `<span style="color:rgba(212,255,0,0.7)">🔍 BUSCANDO: "${escHtml(q)}"</span>`;
  resultsEl.innerHTML = '<div class="search-empty">BUSCANDO...</div>';
  try {
    const data = await API.fetchMinioSearch(q);
    resultsEl.innerHTML = data.results.length === 0 ? '<div class="search-empty">SIN RESULTADOS</div>' : '';
    renderMinioFiles(data.results, resultsEl);
  } catch(e) { resultsEl.innerHTML = `<div class="search-empty">ERROR: ${escHtml(e.message)}</div>`; }
}

export async function browseMinio(prefix) {
  updateState('_minioPrefix', prefix);
  const resultsEl = document.getElementById('minio-results');
  resultsEl.innerHTML = '<div class="search-empty">CARGANDO...</div>';
  renderMinioBreadcrumb(prefix);
  try {
    const data = await API.fetchMinioBrowse(prefix);
    resultsEl.innerHTML = (data.folders.length === 0 && data.files.length === 0) ? '<div class="search-empty">CARPETA VACÍA</div>' : '';
    data.folders.forEach(f => renderMinioFolder(f, resultsEl));
    renderMinioFiles(data.files, resultsEl);
  } catch(e) { resultsEl.innerHTML = `<div class="search-empty">ERROR: ${escHtml(e.message)}</div>`; }
}

// ── RENDERERS ──

function renderMinioBreadcrumb(prefix) {
  const el = document.getElementById('minio-breadcrumb');
  if (!el) return;
  const parts = prefix ? prefix.replace(/\/$/, '').split('/') : [];
  let html = `<span class="minio-crumb" style="cursor:pointer;color:rgba(255,45,85,0.8)">retrotv</span>`;
  let accumulated = '';
  
  el.innerHTML = '';
  const root = document.createElement('span');
  root.className = 'minio-crumb';
  root.style.cssText = 'cursor:pointer;color:rgba(255,45,85,0.8)';
  root.textContent = 'retrotv';
  root.onclick = () => browseMinio('');
  el.appendChild(root);

  parts.forEach((part, i) => {
    accumulated += part + '/';
    const snap = accumulated;
    const sep = document.createElement('span');
    sep.style.color = 'rgba(255,255,255,0.3)';
    sep.textContent = ' / ';
    el.appendChild(sep);

    const crumb = document.createElement('span');
    crumb.className = 'minio-crumb';
    crumb.style.cssText = `cursor:pointer;color:${i === parts.length-1 ? 'rgba(255,255,255,0.8)' : 'rgba(255,45,85,0.8)'}`;
    crumb.textContent = part;
    crumb.onclick = () => browseMinio(snap);
    el.appendChild(crumb);
  });
}

function renderMinioFiles(files, container) {
  const urlChannelMap = {};
  catalog.forEach(ch => {
    (ch.videos || []).forEach(v => {
      if (v.url) {
        if (!urlChannelMap[v.url]) urlChannelMap[v.url] = [];
        urlChannelMap[v.url].push(`CH ${ch.num}`);
      }
    });
  });

  files.forEach(file => {
    const sizeMB = file.size ? (file.size / 1024 / 1024).toFixed(1) + ' MB' : '';
    const inChannels = urlChannelMap[file.url] || [];
    const alreadyAdded = inChannels.length > 0;
    const currentTags = _minioAllTags[file.path] || '';

    const card = document.createElement('div');
    card.className = 'search-result-card minio-file-card';
    card.dataset.path = file.path;

    card.innerHTML = `
      ${alreadyAdded ? `<div class="minio-added-badge" title="En: ${escHtml(inChannels.join(', '))}">✓ ${escHtml(inChannels.join(', '))}</div>` : ''}
      <div class="minio-file-inner">
        <div style="font-size:28px">${file.name.toLowerCase().endsWith('.mp4') ? '🎬' : '📼'}</div>
        <div class="search-result-title" style="text-align:center">${escHtml(file.name)}</div>
        <div class="search-result-channel">${file.name.split('.').pop().toUpperCase()}${sizeMB ? ' · ' + sizeMB : ''}</div>
      </div>
      <div class="minio-file-overlay">
        <button class="btn btn-secondary btn-preview-file">▶ PREVIEW</button>
        <button class="btn btn-primary btn-add-file">＋ AÑADIR</button>
      </div>
      <div class="minio-tags-row" data-path="${escHtml(file.path)}">
        ${renderTagChips(currentTags)}
        <button class="minio-tag-edit-btn" title="Editar tags">＋ TAG</button>
      </div>
    `;

    card.querySelector('.btn-preview-file').onclick = (e) => { e.stopPropagation(); previewMinioFile(file); };
    card.querySelector('.btn-add-file').onclick = (e) => { e.stopPropagation(); addMinioFile(file); };
    card.querySelector('.minio-tag-edit-btn').onclick = (e) => { e.stopPropagation(); openTagEditor(card, file); };

    container.appendChild(card);
  });
}

function renderTagChips(tagsStr) {
  if (!tagsStr) return '';
  return tagsStr.split(',')
    .map(t => t.trim()).filter(Boolean)
    .map(t => `<span class="minio-tag-chip">${escHtml(t)}</span>`)
    .join('');
}

function renderMinioFolder(folder, container) {
  const card = document.createElement('div');
  card.className = 'search-result-card minio-folder-card';
  card.innerHTML = `
    <div class="minio-folder-inner">
      <div style="font-size:32px">📁</div>
      <div class="search-result-title" style="text-align:center">${escHtml(folder.name)}</div>
    </div>
    <button class="minio-folder-add-btn" title="Añadir todos">＋</button>
  `;
  card.querySelector('.minio-folder-add-btn').onclick = (e) => {
    e.stopPropagation(); addMinioFolder(folder.prefix, folder.name);
  };
  card.onclick = () => browseMinio(folder.prefix);
  container.appendChild(card);
}

// ── ACTIONS ──

function previewMinioFile(file) {
  // Reusar preview modal global
  const modal = document.getElementById('preview-modal');
  document.getElementById('preview-title').textContent = file.name;
  const player = document.getElementById('preview-player');
  player.innerHTML = `
    <video controls style="width:100%;height:100%;background:#000" autoplay>
      <source src="${file.url}" type="video/mp4">
    </video>
  `;
  modal.classList.remove('hidden');
}

function addMinioFile(file) {
  closeMinioModal();
  updateState('editingVideoIndex', null);
  document.getElementById('modal-title').textContent = 'AÑADIR VÍDEO';
  document.getElementById('vid-type').value = 'minio';
  // Llamar a onTypeChange global si existe o simplemente mutar DOM
  const fieldYT = document.getElementById('field-yt-id');
  const fieldMinio = document.getElementById('field-minio-url');
  if(fieldYT) fieldYT.classList.add('hidden');
  if(fieldMinio) fieldMinio.classList.remove('hidden');

  document.getElementById('vid-title').value = file.name.replace(/\.[^.]+$/, '');
  document.getElementById('vid-minio-url').value = file.url;
  document.getElementById('video-modal').classList.remove('hidden');
  
  // Intentar obtener duración
  fetchMinioDuration(file.url);
}

async function addMinioFolder(prefix, folderName) {
  showToast(`CARGANDO ${folderName}...`);
  try {
    const data = await API.fetchMinioBrowse(prefix);
    if (data.files.length === 0) { showToast('SIN VÍDEOS', true); return; }
    data.files.forEach(f => {
      catalog[selectedChannel].videos.push({ type: 'minio', url: f.url, title: f.name.replace(/\.[^.]+$/, ''), duration: 0 });
    });
    window.renderVideoList();
    showToast(`${data.files.length} VÍDEOS AÑADIDOS`);
  } catch(e) { showToast('ERROR AL CARGAR CARPETA', true); }
}

async function openTagEditor(card, file) {
  if (card.querySelector('.minio-tag-input-row')) { card.querySelector('.minio-tag-input-row').remove(); return; }
  const tagsRow = card.querySelector('.minio-tags-row');
  const currentTags = _minioAllTags[file.path] || '';
  const row = document.createElement('div');
  row.className = 'minio-tag-input-row';
  row.innerHTML = `
    <input type="text" class="minio-tag-input" value="${escHtml(currentTags)}" onclick="event.stopPropagation()">
    <div style="display:flex;gap:4px">
      <button class="btn btn-primary" style="padding:3px 10px;font-size:11px">✓</button>
      <button class="btn btn-secondary" style="padding:3px 10px;font-size:11px">✕</button>
    </div>
  `;
  const input = row.querySelector('input');
  row.querySelectorAll('button')[0].onclick = async e => { e.stopPropagation(); await _saveTags(file, input.value, card, tagsRow); row.remove(); };
  row.querySelectorAll('button')[1].onclick = e => { e.stopPropagation(); row.remove(); };
  input.onkeydown = async e => {
    if (e.key === 'Enter') { await _saveTags(file, input.value, card, tagsRow); row.remove(); }
    if (e.key === 'Escape') row.remove();
    e.stopPropagation();
  };
  tagsRow.after(row);
  input.focus();
}

async function _saveTags(file, rawTags, card, tagsRow) {
  try {
    const data = await API.saveMinioTags(file, rawTags);
    const newTags = { ..._minioAllTags };
    newTags[file.path] = data.tags;
    updateState('_minioAllTags', newTags);
    tagsRow.innerHTML = renderTagChips(data.tags) + `<button class="minio-tag-edit-btn" title="Editar tags">＋ TAG</button>`;
    tagsRow.querySelector('.minio-tag-edit-btn').onclick = e => { e.stopPropagation(); openTagEditor(card, file); };
    showToast('TAGS GUARDADOS');
  } catch(e) { showToast('ERROR AL GUARDAR TAGS', true); }
}

export async function onMinioUpload(input) {
  const file = input.files[0];
  if (!file) return;
  const progress = document.getElementById('minio-upload-progress');
  progress.style.display = 'block';
  progress.style.color = '#0f8';
  progress.textContent = `⏳ SUBIENDO ${file.name}...`;
  try {
    const result = await API.uploadMinioFile(file, _minioPrefix);
    progress.textContent = `✓ ${result.name} (${(result.size / 1024 / 1024).toFixed(1)} MB)${result.transcoded ? ' · transcodificado' : ''}`;
    browseMinio(_minioPrefix);
  } catch (e) {
    progress.textContent = `✕ ERROR: ${e.message}`;
    progress.style.color = '#ff3366';
  }
  input.value = '';
}

export async function onMinioImportUrl() {
  const input = document.getElementById('minio-import-url-input');
  const url = input.value.trim();
  if (!url) return;
  const progress = document.getElementById('minio-upload-progress');
  progress.style.display = 'block';
  progress.style.color = '#0f8';
  progress.textContent = `⏳ DESCARGANDO ${url}...`;
  try {
    const result = await API.importMinioUrl(url, _minioPrefix);
    progress.textContent = `✓ ${result.name} (${(result.size / 1024 / 1024).toFixed(1)} MB)${result.transcoded ? ' · transcodificado' : ''}`;
    input.value = '';
    browseMinio(_minioPrefix);
  } catch (e) {
    progress.textContent = `✕ ERROR: ${e.message}`;
    progress.style.color = '#ff3366';
  }
}

export async function fetchMinioDuration(url) {
  const statusEl = document.getElementById('minio-duration-status');
  if(!statusEl) return;
  statusEl.textContent = 'OBTENIENDO DURACIÓN...';
  try {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = url;
    video.onloadedmetadata = () => {
      const d = video.duration;
      const m = Math.floor(d / 60);
      const s = Math.round(d % 60);
      statusEl.textContent = `DURACIÓN: ${m}m ${s}s (${Math.round(d)}s)`;
      statusEl.style.color = '#00ff88';
      document.getElementById('vid-duration').value = Math.round(d);
    };
    video.onerror = () => { statusEl.textContent = 'ERROR DE METADATOS'; statusEl.style.color = '#ff3366'; };
  } catch(e) { statusEl.textContent = 'ERROR'; }
}
